import type { Column, ColumnState, GridApi } from 'ag-grid-enterprise'
import type { SortModelItem } from 'ag-grid-enterprise'

/**
 * Shared multi-sort priority for header labels and store round-trips.
 *
 * AG Grid does not always expose clean contiguous `sortIndex` values
 * (`0, 1, 2, …`). During `applyColumnState` / reindexing, several sorted
 * columns can briefly share the same index (e.g. `0, 3, 3, 3`), or indexes
 * can be null when reading column state for the query store.
 *
 * AG Grid's own sort ordering is: lower `sortIndex` first; when indexes are
 * equal or missing, fall back to column display order (left → right). We
 * mirror that here so headers and `orderBy` conversion stay consistent with
 * the grid instead of doing `sortIndex + 1` (which turns `0,3,3,3` into
 * labels `1,4,4,4`).
 *
 * Behavior requirements: `requirements.mdc` (`TSH-R-6`, `TSH-R-7`).
 */

/**
 * Comparator for multi-sort priority (AG Grid's rule).
 *
 * Prefer distinct `sortIndex`; otherwise use `displayOrder` as the tie-break
 * so duplicate/null indexes still produce a stable total order.
 */
export const compareAgGridSortPriority = (
  leftSortIndex: number | null | undefined,
  rightSortIndex: number | null | undefined,
  leftDisplayOrder: number,
  rightDisplayOrder: number,
): number => {
  if (
    leftSortIndex != null &&
    rightSortIndex != null &&
    leftSortIndex !== rightSortIndex
  ) {
    return leftSortIndex - rightSortIndex
  }

  if (leftSortIndex != null && rightSortIndex == null) {
    return -1
  }

  if (leftSortIndex == null && rightSortIndex != null) {
    return 1
  }

  // Equal or both-null sortIndex → column display order (AG Grid tie-break).
  return leftDisplayOrder - rightDisplayOrder
}

/** Orders serialized column state by multi-sort priority (TSH-R-6). */
export const orderColumnStatesBySortPriority = (
  columnStates: ColumnState[],
): ColumnState[] => {
  const sortedEntries = columnStates
    .map((state, displayOrder) => ({ state, displayOrder }))
    .filter(({ state }) => state.sort != null)

  if (sortedEntries.length <= 1) {
    return sortedEntries.map(({ state }) => state)
  }

  return sortedEntries
    .sort((left, right) =>
      compareAgGridSortPriority(
        left.state.sortIndex,
        right.state.sortIndex,
        left.displayOrder,
        right.displayOrder,
      ),
    )
    .map(({ state }) => state)
}

/** Returns live grid columns in multi-sort priority order (TSH-R-6). */
export const getSortedColumnsInPriorityOrder = (api: GridApi): Column[] => {
  const columns = api.getColumns() ?? []
  const sortedEntries = columns
    .map((column, displayOrder) => ({ column, displayOrder }))
    .filter(({ column }) => column.getSort() != null)

  if (sortedEntries.length <= 1) {
    return sortedEntries.map(({ column }) => column)
  }

  return sortedEntries
    .sort((left, right) =>
      compareAgGridSortPriority(
        left.column.getSortIndex(),
        right.column.getSortIndex(),
        left.displayOrder,
        right.displayOrder,
      ),
    )
    .map(({ column }) => column)
}

/**
 * Builds an AG Grid sort model with contiguous `sortIndex` values `0..n-1`
 * in resolved priority order (TSH-R-7).
 *
 * Call this when reading column state for the store: raw `sortIndex` may be
 * null or duplicated, so we reassign indexes from the ordered list rather
 * than trusting the values AG Grid currently exposes.
 */
export const getAgGridSortModelFromColumnState = (
  columnStates: ColumnState[],
): SortModelItem[] =>
  orderColumnStatesBySortPriority(columnStates).map((col, index) => ({
    colId: col.colId!,
    sort: col.sort!,
    sortIndex: index,
  }))

export type TableHeaderSortDisplay = {
  /** Zero-based position in priority order; UI shows `sortOrderIndex + 1`. */
  sortOrderIndex: number | null
  sortedColumnCount: number
}

/**
 * Resolves multi-sort indicator state for a custom table header (TSH-R-1/2/3).
 *
 * Priority is position in {@link getSortedColumnsInPriorityOrder}, not
 * `column.getSortIndex() + 1`, so duplicate indexes still yield unique labels.
 */
export const getTableHeaderSortDisplay = (
  api: GridApi,
  column: Column,
): TableHeaderSortDisplay => {
  const sortedColumns = getSortedColumnsInPriorityOrder(api)
  const sortedColumnCount = sortedColumns.length

  if (column.getSort() == null) {
    return { sortOrderIndex: null, sortedColumnCount }
  }

  const sortOrderIndex = sortedColumns.findIndex(
    sortedColumn => sortedColumn.getColId() === column.getColId(),
  )

  return {
    sortOrderIndex: sortOrderIndex >= 0 ? sortOrderIndex : null,
    sortedColumnCount,
  }
}
